/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * AdminHoursDirectory — admin CRUD for the Métier Hours café directory.
 * DB-backed via /api/admin/hours-cafes. Features:
 *   • Region filter chip bar (dynamic from /api/hours/regions)
 *   • + Add café modal
 *   • Inline row editing
 *   • Featured toggle (top of region + hero eligibility)
 *   • Soft-archive so accidental deletes are recoverable
 */

const PARTNER_STATUSES = [
  { key: "verified_metier_hours",    label: "Verified Métier Hours" },
  { key: "work_friendly_directory",  label: "Work-Friendly Directory" },
];
const VENUE_TYPES = ["cafe", "restaurant", "eatery", "other"];
// Beta punch-list P0-2 · Structured perk types — admin picks the shape and
// fills in the customer-facing label + optional terms.
const PERK_TYPES = [
  { key: "",                    label: "— no perk —" },
  { key: "percentage_discount", label: "Percentage discount" },
  { key: "dollar_discount",     label: "Dollar discount" },
  { key: "free_item",           label: "Free item" },
  { key: "special_offer",       label: "Special offer" },
  { key: "custom",              label: "Custom" },
];

// Session 60.19 · Suggested-tag taxonomy for Métier Hours cafés. Freeform
// tags still work (Liz can type anything) — the chip picker just makes the
// canonical tags one-click. Split into buckets so the picker UI stays
// scannable. Order-stable so admin scanning is predictable.
const TAG_LIBRARY = [
  { group: "Cuisine / Menu", tags: [
    "Coffee-Focused", "Bakery", "Full Menu", "Quick Bites", "Sweet Treats",
    "Thai", "Vietnamese", "Mexican", "Italian", "Mediterranean", "American",
    "Vegetarian", "Vegan", "Gluten-Free Options",
  ]},
  { group: "Vibe / Environment", tags: [
    "Work-Friendly", "Quiet", "Buzzy", "Cozy", "Outdoor Seating", "Rooftop",
    "Group-Friendly", "Kid-Friendly", "Pet-Friendly",
  ]},
  { group: "Amenities", tags: [
    "Wi-Fi", "Power Outlets", "Meeting Space", "Late-Night", "Early-Open",
    "Drive-Thru",
  ]},
  { group: "Ownership / Story", tags: [
    "Locally-Owned", "LGBTQ-Owned", "Woman-Owned", "BIPOC-Owned",
    "Family-Owned", "Veteran-Owned",
  ]},
  { group: "Métier", tags: ["Métier Hours Partner", "Featured"] },
];

function TagChipPicker({ value, onChange, testidPrefix = "tag-chip" }) {
  // `value` is the comma-separated string used by the form; we operate on
  // a Set so toggling is O(1). Preserve any custom tags the admin already
  // typed that aren't in the library.
  const cur = new Set(
    (value || "").split(",").map(s => s.trim()).filter(Boolean)
  );
  const toggle = (t) => {
    const next = new Set(cur);
    if (next.has(t)) next.delete(t); else next.add(t);
    onChange(Array.from(next).join(", "));
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }} data-testid={`${testidPrefix}-picker`}>
      {TAG_LIBRARY.map(({ group, tags }) => (
        <div key={group}>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--m-muted, rgba(27,35,64,0.55))", fontWeight: 600, marginBottom: 4 }}>
            {group}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {tags.map(t => {
              const on = cur.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  data-testid={`${testidPrefix}-${t.replace(/[^a-z0-9]/gi,"-").toLowerCase()}`}
                  style={{
                    padding: "5px 10px",
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 600,
                    border: on ? "1px solid rgba(184,145,58,0.7)" : "1px solid rgba(27,35,64,0.18)",
                    background: on ? "rgba(184,145,58,0.14)" : "transparent",
                    color: on ? "#B8913A" : "inherit",
                    cursor: "pointer",
                  }}
                >
                  {on ? "\u2713 " : "+ "}{t}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY_FORM = {
  name: "", region: "", city: "", state: "", neighborhood: "",
  address: "", hours: "", url: "",
  partner_status: "work_friendly_directory", benefit_description: "",
  venue_type: "cafe", featured: false, tags: "",
  affiliate_label: "", affiliate_url: "", cta_label: "",
  perk_type: "", perk_value: "", perk_label: "", perk_terms: "",
};

export default function AdminHoursDirectory() {
  const [regions, setRegions] = useState([]);
  const [cafes, setCafes] = useState([]);
  const [region, setRegion] = useState("all");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Session 60.25 · Check-Ins analytics (lightweight — reuses the admin
  // JSON endpoint; not a separate dashboard).
  const [checkInsStats, setCheckInsStats] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);

  const loadRegions = async () => {
    try {
      const r = await api.get("/hours/regions");
      setRegions(r.data);
    } catch (e) {
      toast.error("Regions failed to load");
    }
  };
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hours-cafes", { params: { include_archived: includeArchived ? "true" : "false" } });
      setCafes(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadRegions(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  // Session 60.25 · Load check-in analytics lazily — only when the admin
  // opens the panel, to avoid an extra query on every directory visit.
  useEffect(() => {
    if (!statsOpen) return;
    api.get("/admin/hours/check-ins/analytics")
      .then(r => setCheckInsStats(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Check-in analytics failed to load"));
  }, [statsOpen]);

  const grouped = useMemo(() => {
    const filter = region === "all" ? cafes : cafes.filter(c => c.region === region);
    // group by region
    const buckets = {};
    filter.forEach(c => {
      buckets[c.region] = buckets[c.region] || [];
      buckets[c.region].push(c);
    });
    // sort keys by regions order
    const order = regions.map(r => r.key);
    return Object.keys(buckets).sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map(k => ({ region: k, label: regions.find(r => r.key === k)?.label || k, cafes: buckets[k] }));
  }, [cafes, region, regions]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.region) { toast.error("Name and region required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
        perk_value: form.perk_value === "" || form.perk_value == null ? null : Number(form.perk_value),
      };
      await api.post("/admin/hours-cafes", payload);
      toast.success(`${form.name} added`);
      setShowAdd(false); setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.cafe_id);
    setEditForm({
      ...c,
      tags: (c.tags || []).join(", "),
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        tags: editForm.tags ? editForm.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
        perk_value: editForm.perk_value === "" || editForm.perk_value == null ? null : Number(editForm.perk_value),
      };
      delete payload.cafe_id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/admin/hours-cafes/${editingId}`, payload);
      toast.success("Saved");
      cancelEdit();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleFeatured = async (c) => {
    try {
      const r = await api.post(`/admin/hours-cafes/${c.cafe_id}/toggle-featured`);
      toast.success(r.data.featured ? "Featured" : "Un-featured");
      load();
    } catch (e) { toast.error("Toggle failed"); }
  };
  const toggleArchive = async (c) => {
    const label = c.archived ? "restore" : "archive";
    if (!window.confirm(`${c.archived ? "Restore" : "Archive"} ${c.name}?`)) return;
    try {
      await api.post(`/admin/hours-cafes/${c.cafe_id}/archive`);
      toast.success(label + "d");
      load();
    } catch (e) { toast.error("Archive failed"); }
  };

  // Session 60.13 · Alphabetize the state/region chip bar per Cam. "All"
  // stays pinned first and "International" pinned last; every US region in
  // between is sorted A–Z by label so admin scanning is predictable
  // regardless of when the region was seeded.
  const regionChips = useMemo(() => {
    const rest = regions.filter(r => r.key !== "all");
    const intl = rest.filter(r => r.key === "international");
    const others = rest.filter(r => r.key !== "international")
      .slice()
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return [...others, ...intl];
  }, [regions]);

  return (
    <div className="m-admin-section" data-testid="admin-hours-directory">
      <div className="m-card" style={{marginBottom: 16}}>
        <span className="m-eyebrow" style={{color: "var(--gold)"}}>Métier Hours · Directory</span>
        <h2 className="rec-h2" style={{margin: "6px 0 12px"}}>Café listings.</h2>
        <p className="m-muted" style={{maxWidth: 640, marginTop: -4}}>
          The directory is now DB-backed. Edits made here appear immediately on the public <code>/hours</code> page. Featured cafés show at the top of their region and populate the hero collaborator slot when marked verified.
        </p>
        <div style={{display:"flex", gap: 10, alignItems: "center", marginTop: 16, flexWrap: "wrap"}}>
          <button className="m-btn-gold" onClick={() => { setShowAdd(true); setForm(EMPTY_FORM); }} data-testid="hours-cafe-add-open">+ Add café</button>
          <label className="m-checkbox-inline" style={{fontSize:13, color:"var(--m-muted)", display:"flex", gap:6, alignItems:"center"}}>
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="hours-cafe-include-archived" />
            Show archived
          </label>
        </div>
        <div className="m-tabs" style={{marginTop: 18, flexWrap: "wrap", gap: 6}}>
          <button className={"m-tab" + (region === "all" ? " active" : "")} onClick={() => setRegion("all")} data-testid="hours-cafe-region-all">All ({cafes.length})</button>
          {regionChips.map(r => (
            <button key={r.key} className={"m-tab" + (region === r.key ? " active" : "")} onClick={() => setRegion(r.key)} data-testid={`hours-cafe-region-${r.key}`}>
              {r.label} ({cafes.filter(c => c.region === r.key).length})
            </button>
          ))}
        </div>
      </div>

      {/* Session 60.25 · Check-Ins lightweight admin panel. Collapsed by
          default; expanding fetches /admin/hours/check-ins/analytics. */}
      <div className="m-card" style={{ marginBottom: 16 }} data-testid="admin-checkins-panel">
        <button
          type="button"
          onClick={() => setStatsOpen(v => !v)}
          data-testid="admin-checkins-toggle"
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            display: "flex", justifyContent: "space-between", width: "100%",
            alignItems: "center", color: "inherit", textAlign: "left",
          }}
        >
          <span>
            <span className="m-eyebrow" style={{ color: "var(--gold)" }}>Métier Hours · Check-Ins</span>
            <h3 className="m-side-title" style={{ margin: "4px 0 0" }}>Member work-history analytics</h3>
          </span>
          <span style={{ fontSize: 13, color: "var(--gold, #B8913A)", fontWeight: 600 }}>
            {statsOpen ? "Hide ↑" : "Show ↓"}
          </span>
        </button>
        {statsOpen && (
          <div style={{ marginTop: 14 }} data-testid="admin-checkins-body">
            {!checkInsStats ? (
              <div className="m-loading-inline">Loading analytics…</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
                {[
                  { key: "today",        title: "Last 24 hours" },
                  { key: "last_7_days",  title: "Last 7 days"    },
                  { key: "last_30_days", title: "Last 30 days"   },
                ].map(({ key, title }) => {
                  const b = checkInsStats[key] || {};
                  return (
                    <div
                      key={key}
                      data-testid={`admin-checkins-bucket-${key}`}
                      style={{
                        padding: 12,
                        border: "1px solid rgba(184,145,58,0.22)",
                        borderRadius: 10,
                        background: "rgba(184,145,58,0.04)",
                      }}
                    >
                      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--gold, #B8913A)", fontWeight: 600 }}>
                        {title}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 600 }}>
                        {b.total_check_ins || 0} <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>check-ins</span>
                      </div>
                      <div className="m-muted small" style={{ marginTop: 2 }}>
                        {b.unique_members || 0} unique · {b.repeat_members || 0} repeat · {b.commons_shared || 0} shared ({Math.round((b.commons_share_rate || 0) * 100)}%)
                      </div>
                      {Array.isArray(b.top_cafes) && b.top_cafes.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.6, marginBottom: 4 }}>Top cafés</div>
                          {b.top_cafes.slice(0, 5).map((c, i) => (
                            <div key={`${key}-${i}`} className="m-muted small" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name || c.cafe_id || "—"}</span>
                              <strong>{c.check_ins}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                      {Array.isArray(b.work_intents) && b.work_intents.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", opacity: 0.6, marginBottom: 4 }}>Work intents</div>
                          {b.work_intents.slice(0, 6).map((it, i) => (
                            <div key={`${key}-i-${i}`} className="m-muted small" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                              <span>{it.label || it.work_intent || "— unset —"}</span>
                              <strong>{it.count}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="m-loading-inline">Loading directory…</div>
      ) : grouped.length === 0 ? (
        <div className="m-empty">No cafés in this region.</div>
      ) : grouped.map(bucket => (
        <div key={bucket.region} className="m-card" style={{marginBottom: 18}}>
          <h3 className="m-side-title" style={{marginTop:0}}>{bucket.label} <span className="m-muted small">· {bucket.cafes.length}</span></h3>
          <div className="m-admin-list" style={{marginTop: 10}}>
            {bucket.cafes.map(c => editingId === c.cafe_id ? (
              <div key={c.cafe_id} className="m-admin-row" data-testid={`hours-cafe-editing-${c.cafe_id}`} style={{padding: "16px 0", borderBottom: "1px solid rgba(212,168,75,0.08)"}}>
                <div style={{display:"grid", gridTemplateColumns: "1fr 1fr", gap: 10, width: "100%"}}>
                  <label className="m-field"><span>Name</span><input value={editForm.name||""} onChange={e => setEditForm(s => ({...s, name: e.target.value}))} data-testid="cafe-edit-name" /></label>
                  <label className="m-field"><span>Region</span>
                    <select value={editForm.region||""} onChange={e => setEditForm(s => ({...s, region: e.target.value}))} data-testid="cafe-edit-region">
                      {regionChips.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </label>
                  <label className="m-field"><span>Neighborhood</span><input value={editForm.neighborhood||""} onChange={e => setEditForm(s => ({...s, neighborhood: e.target.value}))} /></label>
                  <label className="m-field"><span>City / State label</span><input value={editForm.city||""} onChange={e => setEditForm(s => ({...s, city: e.target.value}))} /></label>
                  <label className="m-field" style={{gridColumn:"span 2"}}><span>Address</span><input value={editForm.address||""} onChange={e => setEditForm(s => ({...s, address: e.target.value}))} /></label>
                  <label className="m-field" style={{gridColumn:"span 2"}}><span>Hours (free-text)</span><input value={editForm.hours||""} onChange={e => setEditForm(s => ({...s, hours: e.target.value}))} placeholder="e.g. Open 7 days · 7am–7pm" /></label>
                  <label className="m-field"><span>Website URL</span><input value={editForm.url||""} onChange={e => setEditForm(s => ({...s, url: e.target.value}))} /></label>
                  <label className="m-field" data-testid="edit-affiliate-url-field">
                    <span>Booking / affiliate URL <span style={{ opacity: 0.6, fontWeight: 400 }}>(overrides Website — leave blank for normal cafés; never paste a social link)</span></span>
                    <input
                      type="url"
                      value={editForm.affiliate_url||""}
                      onChange={e => setEditForm(s => ({...s, affiliate_url: e.target.value}))}
                      placeholder="Leave blank unless the partner has a booking/affiliate URL"
                    />
                  </label>
                  <label className="m-field"><span>Partner status</span>
                    <select value={editForm.partner_status||"work_friendly_directory"} onChange={e => setEditForm(s => ({...s, partner_status: e.target.value}))}>
                      {PARTNER_STATUSES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </label>
                  <label className="m-field"><span>Venue type</span>
                    <select value={editForm.venue_type||"cafe"} onChange={e => setEditForm(s => ({...s, venue_type: e.target.value}))}>
                      {VENUE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="m-field"><span>Tags (comma-separated)</span><input value={editForm.tags||""} onChange={e => setEditForm(s => ({...s, tags: e.target.value}))} /></label>
                  <div className="m-field" style={{ gridColumn: "1 / -1" }}>
                    <span>Or pick from the tag library</span>
                    <TagChipPicker
                      value={editForm.tags}
                      onChange={(v) => setEditForm(s => ({ ...s, tags: v }))}
                      testidPrefix="edit-tag-chip"
                    />
                  </div>
                  <label className="m-field" style={{gridColumn:"span 2"}}><span>Benefit description</span><input value={editForm.benefit_description||""} onChange={e => setEditForm(s => ({...s, benefit_description: e.target.value}))} /></label>
                  {/* Beta punch-list P0-2 · Structured perk. `perk_label` is what members see on the card. */}
                  <label className="m-field"><span>Perk type</span>
                    <select value={editForm.perk_type||""} onChange={e => setEditForm(s => ({...s, perk_type: e.target.value}))} data-testid="cafe-edit-perk-type">
                      {PERK_TYPES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </label>
                  <label className="m-field"><span>Perk value (e.g. 15 for 15%)</span><input type="number" step="0.01" value={editForm.perk_value ?? ""} onChange={e => setEditForm(s => ({...s, perk_value: e.target.value}))} data-testid="cafe-edit-perk-value" /></label>
                  <label className="m-field" style={{gridColumn:"span 2"}}><span>Perk label (customer-facing, e.g. "15% Off")</span><input value={editForm.perk_label||""} onChange={e => setEditForm(s => ({...s, perk_label: e.target.value}))} data-testid="cafe-edit-perk-label" placeholder="15% Off" /></label>
                  <label className="m-field" style={{gridColumn:"span 2"}}><span>Perk terms (optional, shown behind "View terms")</span><input value={editForm.perk_terms||""} onChange={e => setEditForm(s => ({...s, perk_terms: e.target.value}))} data-testid="cafe-edit-perk-terms" placeholder="Show your Métier Hours QR at checkout." /></label>
                </div>
                <div style={{display:"flex", gap: 8, marginTop: 14}}>
                  <button className="m-btn-gold" onClick={saveEdit} disabled={saving} data-testid={`cafe-save-${c.cafe_id}`}>{saving ? "Saving…" : "Save"}</button>
                  <button className="m-btn-ghost" onClick={cancelEdit} data-testid={`cafe-cancel-${c.cafe_id}`}>Cancel</button>
                </div>
              </div>
            ) : (
              <div key={c.cafe_id} className="m-admin-row" data-testid={`hours-cafe-${c.cafe_id}`} style={{padding: "12px 0", borderBottom: "1px solid rgba(212,168,75,0.06)", opacity: c.archived ? 0.5 : 1}}>
                <div className="m-admin-row-info" style={{flex: 1}}>
                  <div className="m-admin-row-name">
                    {c.name}
                    {c.featured && <span className="m-badge-founding tiny" style={{marginLeft: 8}}>Featured</span>}
                    {c.partner_status === "verified_metier_hours" && <span className="m-badge-admin tiny" style={{marginLeft: 6, background:"#c8a24a"}}>Verified</span>}
                    {c.archived && <span className="m-badge-admin tiny" style={{marginLeft: 6, background:"#8a8a8a"}}>Archived</span>}
                  </div>
                  <div className="m-muted small">
                    {c.neighborhood || c.city} · {c.venue_type || "cafe"} · <a href={c.url} target="_blank" rel="noreferrer" style={{color:"var(--gold)"}}>site ↗</a>
                  </div>
                  {c.hours && <div className="m-muted small" style={{opacity:0.75, marginTop: 2}}>{c.hours}</div>}
                </div>
                <div style={{display:"flex", gap: 6, flexWrap:"wrap"}}>
                  <button className="m-btn-ghost small" onClick={() => toggleFeatured(c)} data-testid={`cafe-feature-${c.cafe_id}`}>{c.featured ? "Un-feature" : "Feature"}</button>
                  <button className="m-btn-ghost small" onClick={() => startEdit(c)} data-testid={`cafe-edit-${c.cafe_id}`}>Edit</button>
                  <button className="m-btn-ghost small" onClick={() => toggleArchive(c)} data-testid={`cafe-archive-${c.cafe_id}`}>{c.archived ? "Restore" : "Archive"}</button>
                  <a
                    className="m-btn-ghost small"
                    href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/admin/hours-cafes/${c.cafe_id}/impact-report.pdf?month=${new Date().toISOString().slice(0,7)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`cafe-impact-pdf-${c.cafe_id}`}
                    title="Download monthly impact PDF report"
                  >Impact PDF ↓</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {showAdd && (
        <div className="m-modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="m-modal" onClick={(e) => e.stopPropagation()} data-testid="hours-cafe-add-modal">
            <h3 className="rec-h2">Add café.</h3>
            <form onSubmit={create} style={{display:"grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14}}>
              <label className="m-field"><span>Name *</span><input required value={form.name} onChange={e => setForm(s => ({...s, name: e.target.value}))} data-testid="cafe-add-name" /></label>
              <label className="m-field"><span>Region *</span>
                <select required value={form.region} onChange={e => setForm(s => ({...s, region: e.target.value}))} data-testid="cafe-add-region">
                  <option value="">—</option>
                  {regionChips.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </label>
              <label className="m-field"><span>Neighborhood</span><input value={form.neighborhood} onChange={e => setForm(s => ({...s, neighborhood: e.target.value}))} placeholder="e.g. Boonton, NJ" /></label>
              <label className="m-field"><span>City / State label</span><input value={form.city} onChange={e => setForm(s => ({...s, city: e.target.value}))} placeholder="usually just the state name" /></label>
              <label className="m-field" style={{gridColumn:"span 2"}}><span>Full address</span><input value={form.address} onChange={e => setForm(s => ({...s, address: e.target.value}))} placeholder="Street, City, State ZIP" /></label>
              <label className="m-field" style={{gridColumn:"span 2"}}><span>Hours (free-text)</span><input value={form.hours} onChange={e => setForm(s => ({...s, hours: e.target.value}))} placeholder="e.g. Open 7 days · 7am–7pm" /></label>
              <label className="m-field"><span>Website URL</span><input type="url" value={form.url} onChange={e => setForm(s => ({...s, url: e.target.value}))} placeholder="https://…" /></label>
              <label className="m-field" data-testid="new-affiliate-url-field">
                <span>Booking / affiliate URL <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — overrides Website; never paste a social link)</span></span>
                <input
                  type="url"
                  value={form.affiliate_url}
                  onChange={e => setForm(s => ({...s, affiliate_url: e.target.value}))}
                  placeholder="Leave blank unless the partner has a booking/affiliate URL"
                />
              </label>
              <label className="m-field"><span>Partner status</span>
                <select value={form.partner_status} onChange={e => setForm(s => ({...s, partner_status: e.target.value}))}>
                  {PARTNER_STATUSES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </label>
              <label className="m-field"><span>Venue type</span>
                <select value={form.venue_type} onChange={e => setForm(s => ({...s, venue_type: e.target.value}))}>
                  {VENUE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label className="m-field"><span>Tags (comma-separated)</span><input value={form.tags} onChange={e => setForm(s => ({...s, tags: e.target.value}))} placeholder="Work-Friendly, Live Music" /></label>
              <div className="m-field" style={{ gridColumn: "1 / -1" }}>
                <span>Or pick from the tag library</span>
                <TagChipPicker
                  value={form.tags}
                  onChange={(v) => setForm(s => ({ ...s, tags: v }))}
                  testidPrefix="add-tag-chip"
                />
              </div>
              <label className="m-field" style={{gridColumn:"span 2"}}><span>Benefit description</span><input value={form.benefit_description} onChange={e => setForm(s => ({...s, benefit_description: e.target.value}))} placeholder="e.g. 10% off espresso drinks for Founding Members" /></label>
              {/* Beta punch-list P0-2 · Structured perk fields */}
              <label className="m-field"><span>Perk type</span>
                <select value={form.perk_type} onChange={e => setForm(s => ({...s, perk_type: e.target.value}))} data-testid="cafe-add-perk-type">
                  {PERK_TYPES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </label>
              <label className="m-field"><span>Perk value (e.g. 15 for 15%)</span><input type="number" step="0.01" value={form.perk_value} onChange={e => setForm(s => ({...s, perk_value: e.target.value}))} data-testid="cafe-add-perk-value" placeholder="15" /></label>
              <label className="m-field" style={{gridColumn:"span 2"}}><span>Perk label (customer-facing)</span><input value={form.perk_label} onChange={e => setForm(s => ({...s, perk_label: e.target.value}))} data-testid="cafe-add-perk-label" placeholder="15% Off" /></label>
              <label className="m-field" style={{gridColumn:"span 2"}}><span>Perk terms (optional)</span><input value={form.perk_terms} onChange={e => setForm(s => ({...s, perk_terms: e.target.value}))} data-testid="cafe-add-perk-terms" placeholder="Show your Métier Hours QR at checkout." /></label>
              <label className="m-checkbox-inline" style={{gridColumn:"span 2", display:"flex", gap:8, alignItems:"center", marginTop: 4}}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm(s => ({...s, featured: e.target.checked}))} data-testid="cafe-add-featured" />
                <span>Feature this café (top of region + hero collaborator slot when verified)</span>
              </label>
              <div style={{gridColumn:"span 2", display: "flex", gap: 8, marginTop: 6}}>
                <button type="submit" className="m-btn-gold" disabled={saving} data-testid="cafe-add-submit">{saving ? "Adding…" : "Add café"}</button>
                <button type="button" className="m-btn-ghost" onClick={() => setShowAdd(false)} data-testid="cafe-add-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
